import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  FileText,
  Upload,
  Download,
  RotateCcw,
  Loader2,
  CheckCircle2,
  Calendar,
  User as UserIcon,
  Pencil,
  Plus,
  Trash2,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listAnnexureVersions,
  uploadAnnexureVersion,
  rollbackAnnexureVersion,
  editAnnexureText,
  previewAnnexureEdit,
  downloadAnnexureVersionUrl,
} from '../api';

const DESIGNATIONS = [
  { key: 'DC', label: 'Digital Consultant', hint: 'Used for DC / Intern / Sales Exec offers' },
  { key: 'TL', label: 'Team Lead', hint: 'Used for any TL / Team Lead designation' },
];

const formatBytes = (n) => {
  if (!n && n !== 0) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatEffective = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

const AnnexureVersionHistoryDialog = ({ open, onOpenChange, currentUserRole = 'admin' }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadFor, setUploadFor] = useState(null); // 'DC' | 'TL' | null
  const [pdfFile, setPdfFile] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [rollbackId, setRollbackId] = useState(null);
  const [editFor, setEditFor] = useState(null); // 'DC' | 'TL' | null
  const [edits, setEdits] = useState([{ find: '', replace: '' }]);
  const [editEffectiveDate, setEditEffectiveDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editing, setEditing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewImages, setPreviewImages] = useState([]); // [{page_number, image_base64}]

  const isAdmin = currentUserRole === 'admin';
  const isAdminOrHR = currentUserRole === 'admin' || currentUserRole === 'hr_manager';

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listAnnexureVersions();
      setVersions(data.versions || []);
    } catch (e) {
      toast.error(`Failed to load annexure versions: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchVersions();
    } else {
      setUploadFor(null);
      setPdfFile(null);
      setEffectiveDate('');
      setNotes('');
      setEditFor(null);
      setEdits([{ find: '', replace: '' }]);
      setEditEffectiveDate('');
      setEditNotes('');
      setPreviewImages([]);
    }
  }, [open, fetchVersions]);

  // Clear stale preview whenever the user edits any Find/Replace input
  // so they must re-preview after any change.
  useEffect(() => {
    setPreviewImages([]);
  }, [edits]);

  const cleanEdits = () =>
    edits
      .map((e) => ({ find: (e.find || '').trim(), replace: e.replace || '' }))
      .filter((e) => e.find.length > 0);

  const handlePreview = async () => {
    if (!editFor) return;
    const ce = cleanEdits();
    if (ce.length === 0) {
      toast.error('Add at least one text edit');
      return;
    }
    setPreviewing(true);
    setPreviewImages([]);
    try {
      const { data } = await previewAnnexureEdit({
        designation: editFor,
        edits: ce,
      });
      setPreviewImages(data.previews || []);
      toast.success(
        `Preview ready — ${data.applied_edits} edit${data.applied_edits === 1 ? '' : 's'} on ${data.affected_pages?.length || 0} page${data.affected_pages?.length === 1 ? '' : 's'}`
      );
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        Array.isArray(detail)
          ? `Text not found: ${detail.join(', ')}`
          : `Preview failed: ${detail || e.message}`
      );
    } finally {
      setPreviewing(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editFor) return;
    const ce = cleanEdits();
    if (ce.length === 0) {
      toast.error('Add at least one text edit');
      return;
    }
    if (!editEffectiveDate) {
      toast.error('Effective date is required');
      return;
    }
    setEditing(true);
    try {
      const { data } = await editAnnexureText({
        designation: editFor,
        edits: ce,
        effective_date: editEffectiveDate,
        notes: editNotes,
      });
      toast.success(
        `Published ${editFor} v${data.version_number} — ${data.applied_edits} edit${data.applied_edits === 1 ? '' : 's'} applied.`
      );
      setEditFor(null);
      setEdits([{ find: '', replace: '' }]);
      setEditEffectiveDate('');
      setEditNotes('');
      setPreviewImages([]);
      fetchVersions();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        Array.isArray(detail)
          ? `Text not found: ${detail.join(', ')}`
          : `Edit failed: ${detail || e.message}`
      );
    } finally {
      setEditing(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFor || !pdfFile || !effectiveDate) {
      toast.error('Designation, PDF file and effective date are required');
      return;
    }
    if (pdfFile.type !== 'application/pdf') {
      toast.error('Only PDF files allowed');
      return;
    }
    if (pdfFile.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('designation', uploadFor);
      fd.append('effective_date', effectiveDate);
      fd.append('notes', notes);
      fd.append('file', pdfFile);
      const { data } = await uploadAnnexureVersion(fd);
      toast.success(`Published ${uploadFor} v${data.version_number} — effective ${formatEffective(data.effective_date)}`);
      setUploadFor(null);
      setPdfFile(null);
      setEffectiveDate('');
      setNotes('');
      fetchVersions();
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRollback = async (v) => {
    if (!isAdmin) {
      toast.error('Admin only');
      return;
    }
    if (!window.confirm(`Roll back to ${v.designation} v${v.version_number} (${formatEffective(v.effective_date)})?\n\nThe current version will be archived; all new offer letters will use this file immediately.`)) {
      return;
    }
    setRollbackId(v.id);
    try {
      await rollbackAnnexureVersion(v.id);
      toast.success(`Rolled back to ${v.designation} v${v.version_number}`);
      fetchVersions();
    } catch (e) {
      toast.error(`Rollback failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRollbackId(null);
    }
  };

  const grouped = DESIGNATIONS.map((d) => ({
    ...d,
    rows: versions
      .filter((v) => v.designation === d.key)
      .sort((a, b) => (b.version_number || 0) - (a.version_number || 0)),
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="annexure-version-history-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="text-indigo-600" size={22} />
            Annexure Version History (Salary Annexure A)
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Track every revision of the DC and TL salary annexures. Publish a new version to update all
            future offer letters, or roll back to a previous version if a revision needs to be reverted.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 justify-center py-10 text-slate-500">
            <Loader2 className="animate-spin" size={20} /> Loading versions…
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map((group) => (
              <div key={group.key} className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">
                        {group.label}
                      </span>
                      <Badge variant="outline" className="text-xs font-mono">{group.key}</Badge>
                      <span className="text-xs text-slate-500">
                        · {group.rows.length} version{group.rows.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{group.hint}</div>
                  </div>
                  {isAdminOrHR && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditFor(group.key);
                          setEdits([{ find: '', replace: '' }]);
                          setEditEffectiveDate(new Date().toISOString().slice(0, 10));
                          setEditNotes('');
                        }}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        title="Edit clause text in-place — layout, font, logo preserved"
                        data-testid={`edit-text-${group.key.toLowerCase()}-btn`}
                      >
                        <Pencil size={14} className="mr-1" /> Edit Text
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setUploadFor(group.key)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid={`publish-new-${group.key.toLowerCase()}-btn`}
                      >
                        <Upload size={14} className="mr-1" /> Publish New {group.key}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="divide-y">
                  {group.rows.length === 0 && (
                    <div className="px-4 py-6 text-center text-slate-500 text-sm">
                      No versions published yet for {group.label}.
                    </div>
                  )}
                  {group.rows.map((v) => (
                    <div
                      key={v.id}
                      className={`px-4 py-3 flex flex-wrap gap-3 items-center ${
                        v.is_current ? 'bg-emerald-50/50' : 'bg-white'
                      }`}
                      data-testid={`annexure-version-row-${v.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-[80px]">
                        <span className="text-lg font-bold text-slate-700">v{v.version_number}</span>
                        {v.is_current && (
                          <Badge className="bg-emerald-600 text-white gap-1">
                            <CheckCircle2 size={12} /> Current
                          </Badge>
                        )}
                      </div>
                      <div className="flex-1 min-w-[220px]">
                        <div className="text-sm font-medium text-slate-800 break-all">
                          {v.original_filename}
                        </div>
                        {v.notes && (
                          <div className="text-xs text-slate-500 mt-0.5 italic">&ldquo;{v.notes}&rdquo;</div>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 min-w-[140px]">
                        <div className="flex items-center gap-1">
                          <Calendar size={12} className="text-slate-400" />
                          Effective: <b>{formatEffective(v.effective_date)}</b>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <UserIcon size={12} className="text-slate-400" />
                          {v.uploaded_by_name || '-'}
                        </div>
                        <div className="text-slate-400 mt-0.5">
                          Uploaded {formatDate(v.uploaded_at)} · {formatBytes(v.file_size)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={downloadAnnexureVersionUrl(v.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center px-2 py-1 rounded border text-xs hover:bg-slate-100"
                          data-testid={`download-annexure-version-${v.id}`}
                        >
                          <Download size={12} className="mr-1" /> Download
                        </a>
                        {isAdmin && !v.is_current && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRollback(v)}
                            disabled={rollbackId === v.id}
                            className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                            data-testid={`rollback-annexure-version-${v.id}`}
                          >
                            {rollbackId === v.id ? (
                              <Loader2 className="animate-spin" size={12} />
                            ) : (
                              <RotateCcw size={12} className="mr-1" />
                            )}
                            Rollback
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upload sub-dialog */}
        <Dialog open={!!uploadFor} onOpenChange={(o) => !o && setUploadFor(null)}>
          <DialogContent className="max-w-lg" data-testid="publish-annexure-dialog">
            <DialogHeader>
              <DialogTitle>Publish New {uploadFor} Annexure Version</DialogTitle>
              <p className="text-sm text-slate-500">
                The uploaded PDF becomes the active salary annexure for all new offer letters
                immediately. Previous versions remain archived and can be rolled back to.
              </p>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>PDF File <span className="text-red-500">*</span></Label>
                <Input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                  data-testid="new-annexure-file-input"
                />
                {pdfFile && (
                  <div className="text-xs text-slate-500 mt-1">
                    {pdfFile.name} · {formatBytes(pdfFile.size)}
                  </div>
                )}
              </div>
              <div>
                <Label>Effective Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  data-testid="new-annexure-effective-date"
                />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Reason for this revision, HR change ticket, etc."
                  rows={2}
                  data-testid="new-annexure-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadFor(null)} disabled={uploading}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || !pdfFile || !effectiveDate}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="publish-annexure-submit-btn"
              >
                {uploading ? <Loader2 className="animate-spin mr-1" size={14} /> : <Upload size={14} className="mr-1" />}
                Publish {uploadFor} Version
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit-Text sub-dialog — find/replace clauses in the active PDF
            while preserving layout, font, size, logo, watermark. */}
        <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
          <DialogContent className="max-w-2xl" data-testid="edit-annexure-text-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil size={18} className="text-amber-600" />
                Edit Clause Text — {editFor} Annexure
              </DialogTitle>
              <p className="text-sm text-slate-500 mt-1">
                Find text in the current annexure and replace or remove it. The system
                preserves the original font, size, colour, alignment, watermark and
                logo — only the specified text is changed. A new version is published;
                the previous one is archived and can be rolled back anytime.
              </p>
            </DialogHeader>

            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
              {edits.map((edit, idx) => (
                <div
                  key={idx}
                  className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50"
                  data-testid={`edit-row-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-600">
                      Edit #{idx + 1}
                    </span>
                    {edits.length > 1 && (
                      <button
                        type="button"
                        onClick={() =>
                          setEdits((prev) => prev.filter((_, i) => i !== idx))
                        }
                        className="text-red-600 hover:text-red-800"
                        data-testid={`remove-edit-${idx}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Find text (must match exactly) <span className="text-red-500">*</span></Label>
                    <Input
                      value={edit.find}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEdits((prev) =>
                          prev.map((row, i) => (i === idx ? { ...row, find: val } : row))
                        );
                      }}
                      placeholder='e.g. "INR 60,000"'
                      data-testid={`edit-find-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">
                      Replace with <span className="text-slate-400">(leave empty to remove)</span>
                    </Label>
                    <Input
                      value={edit.replace}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEdits((prev) =>
                          prev.map((row, i) => (i === idx ? { ...row, replace: val } : row))
                        );
                      }}
                      placeholder='e.g. "As per defined targets."'
                      data-testid={`edit-replace-${idx}`}
                    />
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setEdits((prev) => [...prev, { find: '', replace: '' }])
                }
                className="w-full border-dashed"
                data-testid="add-another-edit-btn"
              >
                <Plus size={14} className="mr-1" /> Add another edit
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-xs">Effective Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={editEffectiveDate}
                  onChange={(e) => setEditEffectiveDate(e.target.value)}
                  data-testid="edit-effective-date"
                />
              </div>
              <div>
                <Label className="text-xs">Notes (optional)</Label>
                <Input
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Reason / ticket / summary"
                  data-testid="edit-notes"
                />
              </div>
            </div>

            {previewImages.length > 0 && (
              <div className="mt-4 border border-emerald-200 bg-emerald-50/40 rounded-lg p-3">
                <div className="text-xs font-semibold text-emerald-800 mb-2 flex items-center gap-1">
                  <Eye size={12} /> Preview — {previewImages.length} page{previewImages.length === 1 ? '' : 's'} affected. Confirm before publishing.
                </div>
                <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                  {previewImages.map((p) => (
                    <div
                      key={p.page_number}
                      className="border border-slate-200 rounded overflow-hidden bg-white"
                      data-testid={`preview-page-${p.page_number}`}
                    >
                      <div className="text-[10px] uppercase tracking-wide bg-slate-100 px-2 py-1 text-slate-600">
                        Page {p.page_number}
                      </div>
                      <img
                        src={`data:image/png;base64,${p.image_base64}`}
                        alt={`Preview of page ${p.page_number}`}
                        className="w-full h-auto"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setEditFor(null)} disabled={editing || previewing}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handlePreview}
                disabled={editing || previewing}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="preview-edit-text-btn"
              >
                {previewing ? (
                  <Loader2 className="animate-spin mr-1" size={14} />
                ) : (
                  <Eye size={14} className="mr-1" />
                )}
                Preview
              </Button>
              <Button
                onClick={handleEditSubmit}
                disabled={editing || previewing}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="submit-edit-text-btn"
              >
                {editing ? (
                  <Loader2 className="animate-spin mr-1" size={14} />
                ) : (
                  <Pencil size={14} className="mr-1" />
                )}
                Apply Edits &amp; Publish
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default AnnexureVersionHistoryDialog;
